import type {
  ComponentProps, FC,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from '~/_shared/utils/hooks';
import { DEFAULT_RANGES_COUNT } from '~/boundary/settings/useBoundaryFillSettingsPropsVariants';
import { useIsNumericColumnCheck } from '~/heatMap/panel/form/useIsNumericColumnCheck.hook';
import type { ModalProps } from '~/modal/modalType.enum';
import { createBoundaryTerritoryGroupRequestParams } from '~/sidebar/sidebarApps/mapTools/boundary/boundaryTerritoryGroup.helpers';
import { CustomizeBoundaryFillComponent } from '~/sidebar/sidebarApps/mapTools/boundary/customizeFills/customizeBoundaryFill.component';
import { useVerifyNumericFillData } from '~/sidebar/sidebarApps/mapTools/boundary/fill/useVerifyNumericFillData';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import {
  boundaryTerritoryGroupActivate,
  boundaryTerritoryGroupsCreateRequest,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  useActiveBoundaryTerritoryGroupsSelector, useBoundaryTerritoryGroupsIsLoadingSelector,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';

export type AddBoundaryFillContainerProps = ModalProps<{
  boundaryGroupId: BoundaryGroupId;
  onBtgCreated?: (btg: BoundaryTerritoryGroup) => void;
}>;

export const AddBoundaryFillContainer: FC<AddBoundaryFillContainerProps> = ({
  boundaryGroupId,
  isOpen,
  onClose,
  onBtgCreated,
}) => {
  const [t] = useTranslation();
  const { checkIsNumeric, isLoading: isCheckNumericLoading } = useIsNumericColumnCheck();
  const { verifyNumericFillData } = useVerifyNumericFillData({ checkIsNumeric });
  const isBoundaryTerritoryGroupsLoading = useBoundaryTerritoryGroupsIsLoadingSelector();
  const activeBoundaryTerritoryGroup = useActiveBoundaryTerritoryGroupsSelector()
    .find(btg => btg.boundaryGroupId === boundaryGroupId);
  const mapId = useMapIdSelector();
  const dispatch = useDispatch();

  const handleConfirm: ComponentProps<typeof CustomizeBoundaryFillComponent>['onConfirm'] = ({
    boundaryFillData,
    numericCalculateBucketFunction,
    boundaryTerritoryType,
    demographicId,
  }, doNotClose) => {
    if (mapId === null) {
      return;
    }

    const onSuccess = (boundaryTerritoryGroup: BoundaryTerritoryGroup) => {
      // Close the modal
      if (!doNotClose) {
        onClose();
      }
      // Set the newly created BTG as active
      dispatch(boundaryTerritoryGroupActivate(boundaryTerritoryGroup));
      onBtgCreated?.(boundaryTerritoryGroup);
    };

    const createAndDispatch = () => {
      const requestParams = createBoundaryTerritoryGroupRequestParams({
        mapId,
        boundaryGroupId,
        territoryType: boundaryTerritoryType,
        fillData: boundaryFillData,
        demographicId,
        numericCalculateBucketFunction,
      }, activeBoundaryTerritoryGroup);

      dispatch(boundaryTerritoryGroupsCreateRequest(requestParams, onSuccess));
    };

    // Handle numeric territory type with validation
    if (boundaryTerritoryType === BoundaryTerritoryType.Numeric && boundaryFillData) {
      verifyNumericFillData(boundaryFillData)
        .then(isNumeric => {
          if (isNumeric) {
            createAndDispatch();
          }
        });
    }
    else {
      // Handle all other territory types
      createAndDispatch();
    }
  };

  return (
    <CustomizeBoundaryFillComponent
      boundaryFillData={null}
      boundaryGroupId={boundaryGroupId}
      boundaryTerritoryType={null}
      fixedRangeCount={DEFAULT_RANGES_COUNT}
      initialDemographicId={null}
      initialNumericCalculateBucketFunction={null}
      isLoading={isBoundaryTerritoryGroupsLoading || isCheckNumericLoading}
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleConfirm}
      title={t('altfills.addByData')}
    />
  );
};
